<template>
  <div
    :id="`resource-${resource.resource_uuid}`"
    class="dea-card-resource"
    data-test="card-resource-item"
    :data-test-id="`card-resource-${resource.resource_uuid}`"
    :class="[
      { 'dea-card-resource--compact': layout === 'compressed' },
      { 'dea-card-resource--light': layout === 'light' },
      { 'dea-card-resource--clickable': cardClickEvent },
      { 'dea-card-resource--wait': usePopUnder },
      { 'dea-card-resource--locked': isLocked },
      { 'dea-card-resource--no-image': !displayImage },
      [`dea-card-resource--${accentModifier}`],
    ]"
  >
    <div v-if="showBookmarkOrigin && origin && origin.label" class="dea-card-resource__badge-container">
      <div
        :class="`dea-card-resource__badge ${badgeAccentModifier} dea-card-resource__badge--${badgeEmptyModifier}`"
        :title="origin.label"
      >
        <!-- The looping code has been preserved for future extensions -->
        <template v-if="!origin.label">&nbsp;</template>
        <template v-for="(tag, index) in [origin.label]">
          <template v-if="index > 0">,</template>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <span :key="`origin-${index}`" class="dea-heading--medium-xxs" v-html="tag"></span>
        </template>
      </div>
    </div>
    <div
      v-if="resource.title"
      class="dea-card-resource__wrapper"
      @[cardClickEvent]="
        openResource(getLink(mainAsset), isLocked, getTechnicalType(resource), getLearningType(resource))
      "
    >
      <div
        v-if="displayImage"
        class="dea-card-resource__image-container"
        :class="{ 'dea-card-resource__image-container--has-chips': hasChips }"
      >
        <span
          v-if="resource.image && showImage && showIcon"
          class="dea-card-resource__icon"
          :class="`${resource.type_icon} ${iconAccentClass}`"
        ></span>
        <img
          v-if="resource.image"
          class="dea-card-resource__image"
          :alt="resource.title"
          :src="resource.image"
          width="234"
          height="145"
        />
      </div>

      <div class="dea-card-resource__content">
        <div class="dea-card-resource__card-header">
          <div class="dea-card-resource__card-header--left">
            <span
              v-if="(!showImage || !resource.image) && showIcon && layout !== 'compressed'"
              class="dea-card-resource__icon"
              :class="`${resource.type_icon} ${iconAccentClass}`"
            ></span>
            <div class="dea-card-resource__section">
              <div
                v-if="tag1List.length"
                class="dea-card-resource__tag"
                :class="{ 'dea-card-resource__tag--no-image': !displayImage }"
              >
                <template v-for="(tag, index) in tag1List">
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    :key="`tag1-${index}`"
                    class="dea-heading--medium-xxs dea-card-resource__tag-label"
                    v-html="tag"
                  ></span>
                  <!-- eslint-enable vue/no-v-html -->
                  <span
                    v-if="showPipeSeparator(index)"
                    :key="`tag1-separator-${index}`"
                    :class="`dea-card-resource__pipe-separator dea-card-resource__pipe-separator--${accentModifier}`"
                    >&nbsp;|&nbsp;
                  </span>
                </template>
              </div>
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div
                class="dea-card-resource__title"
                :class="{
                  'dea-card-resource__title--no-tag': !tag1List.length,
                  'dea-card-resource__title--multi-tag': tag1List.length > 1,
                }"
                v-html="resource.title"
              ></div>
              <template v-if="tag2List.length">
                <template v-for="(tag, index) in tag2List"
                  ><template v-if="index > 0">,&nbsp;</template>
                  <!-- eslint-disable vue/no-v-html -->
                  <span
                    :key="`tag2-${index}`"
                    class="dea-card-resource__second-tag dea-heading--regular-s"
                    v-html="tag"
                  ></span>
                  <!-- eslint-enable vue/no-v-html -->
                </template>
              </template>
              <div v-if="showCardInfos" class="dea-card-resource__info">
                <p v-if="resource.description" class="dea-card-resource__description">
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span class="dea-heading--regular-s" v-html="resource.description"></span>
                </p>

                <p
                  v-if="getHighlightText()"
                  class="dea-heading--light__xs dea-card-resource__highlight-text"
                  :class="`dea-card-resource__highlight-text--${accentModifier}`"
                >
                  <span v-if="highlightIcon" :class="`dea-ico-${resource[highlightIcon]}`"></span>
                  <!-- eslint-disable-next-line vue/no-v-html -->
                  <span v-html="getHighlightText()"></span>
                </p>
                <div class="dea-card-resource__info-action">
                  <div class="dea-card-resource__chip-container">
                    <template v-if="tag4List.length">
                      <Chips
                        v-for="(chip, index) in tag4List"
                        :key="`chip-tag4-${index}`"
                        class="dea-chip--small"
                        :class="`dea-card-resource__chip--${accentModifier}`"
                      >
                        {{ chip }}
                      </Chips>
                    </template>
                    <template v-if="tag3List.length">
                      <Chips v-for="(chip, index) in tag3List" :key="`chip-${index}`" class="dea-chip--small">
                        {{ chip }}
                      </Chips>
                    </template>
                  </div>
                  <CardResourceActionButtons
                    v-if="displayImage && layout === 'compressed'"
                    class="dea-card-resource__action-buttons-bottom"
                    :resource="resource"
                    :layout="layout"
                    :teachers-only="false"
                    :is-locked="isLocked"
                    :share-enabled="shareEnabledComputed"
                    :show-type-icon="false"
                    :bookmark-enabled="bookmarkEnabled"
                    :bookmark-button-status="bookmarkButtonStatus"
                    @resource-locked="resourceLocked"
                    @delete-bookmark="deleteBookmark"
                    @save-bookmark="saveBookmark"
                    @dropdown-toggled="$emit('dropdown-toggled', $event)"
                  />
                  <div
                    v-if="useAccordion"
                    class="dea-card-resource__accordion-icon"
                    @click.stop.prevent="expandAccordion"
                  >
                    <span
                      class="icon dea-ico-freccia-down dea-accordion__icon"
                      :class="`icon-color--${accentModifier}`"
                    ></span>
                  </div>
                </div>
                <div v-if="useAccordion && isExpanded" class="dea-card-resource__accordion-content">
                  <slot name="accordion-content" />
                </div>
              </div>
            </div>
          </div>

          <CardResourceActionButtons
            v-if="!displayImage || layout !== 'compressed'"
            :resource="resource"
            :teachers-resource-warning="teachersResourceWarning"
            :show-image="showImage"
            :show-type-icon="showIcon"
            :layout="layout"
            :icon-accent-class="iconAccentClass"
            :teachers-only="teachersOnly"
            :show-teacher-label="showTeacherLabel"
            :is-locked="isLocked"
            :share-enabled="shareEnabledComputed"
            :bookmark-enabled="bookmarkEnabled && !multiResource"
            :bookmark-button-status="bookmarkButtonStatus"
            @resource-locked="resourceLocked"
            @delete-bookmark="deleteBookmark"
            @save-bookmark="saveBookmark"
            @dropdown-toggled="$emit('dropdown-toggled', $event)"
          />
        </div>
      </div>
      <div
        v-if="multiResource"
        class="dea-card-resource__body"
        :class="{ 'dea-card-resource__body--indented': !displayImage }"
      >
        <div class="dea-card-resource__buttons-container">
          <Buttons
            v-for="(link, index) in linkedAssets"
            :key="`linkedAssets-${index}`"
            class="dea-button--outline dea-button--small"
            :icon="link.icon"
            type="button"
            target="_blank"
            @click="openResource(getLink(link), false, getTechnicalType(link), getLearningType(link))"
            >{{ link.title }}</Buttons
          >
          <Buttons
            v-for="(link, index) in lockedAssets"
            :key="`lockedAssets-${index}`"
            class="dea-button--outline dea-button--small"
            icon="locker"
            type="button"
            @click="resourceLocked"
            >{{ link.title }}</Buttons
          >
        </div>
      </div>
    </div>
    <div v-if="overlayVisible && url" class="dea-modal-overlay--visible">
      <HeaderFlow :title="resource.title" :logos="logos" @closed="closeOverlay"></HeaderFlow>
      <div class="dea-card-resource__iframe-container">
        <iframe :src="url" frameborder="0" allow="fullscreen"></iframe>
      </div>
    </div>
    <div v-if="url" class="dea-modal-overlay--hidden">
      <div class="dea-card-resource__iframe-container">
        <iframe style="display: none" :src="url" frameborder="0" allow="fullscreen"></iframe>
      </div>
    </div>
  </div>
</template>

<script>
import Buttons from "@/components/elements/Buttons";
import Chips from "@/components/elements/Chips";
import HeaderFlow from "@/components/patterns/HeaderFlow";
import variables from "@/styles/variables.scss";
import CardResourceActionButtons from "@/components/patterns/CardResourceActionButtons";

/**
 * Generic resource
 *
 * Load google class js in explicit mode to enable it. See https://developers.google.com/classroom/guides/sharebutton
 *
 * @version 2.0.1
 */
export default {
  name: "CardResource",
  components: { Buttons, Chips, HeaderFlow, CardResourceActionButtons },
  props: {
    /**
     * Resource to render
     */
    resource: {
      type: Object,
      required: true,
    },
    /**
     * Flag to show type_format icon
     */
    showTypeIcon: {
      type: [Boolean, Function],
      default: true,
    },
    /**
     * Name of the resource attribute to use as source of the tags list or function to format the tags list [tag 1 position]
     */
    tag1: {
      type: [String, Function],
      default: "",
    },
    /**
     * Name of the resource attribute to use as source of the tags list or function to format the tags list [tag 2 position]
     */
    tag2: {
      type: [String, Function],
      default: "",
    },
    /**
     * List of resource attributes to use as source of the tags list or function to format the tags list [tag 3 position]
     */
    chips: {
      type: [Array, Function],
      default: () => [],
    },
    /**
     * Name of the resource attribute to use as source of the tags list or function to format the tags list [tag 4 position]
     */
    tag4: {
      type: [Array, String, Function],
      default: () => [],
    },
    /**
     * Flag to show the bookmark button
     */
    bookmarkEnabled: {
      type: Boolean,
      default: true,
    },
    /**
     * Resource has been bookmarked
     */
    bookmark: {
      type: Object,
      default: () => {},
    },
    /**
     * Flag to show the share button with dropdown
     */
    shareEnabled: {
      type: [Boolean, Function],
      default: true,
    },
    /**
     * Flag to show the resource image
     */
    showImage: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to show the resource image on 'compressed' layout
     */
    showImageOnCompressed: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag to show the teacher label for teacher resources
     */
    showTeacherLabel: {
      type: Boolean,
      default: true,
    },
    /**
     * Name of the resource attribute to use as class to show the icon in the highlight section
     */
    highlightIcon: {
      type: String,
      default: "",
    },
    /**
     * Name of the resource attribute to use as source of the highlight text section or function to format the highlight text section
     */
    highlightText: {
      type: [String, Function],
      default: "",
    },
    /**
     * CardResource layout selection. Possible values: "expanded", "light", "compressed".
     */
    cardLayout: {
      type: String,
      default: "expanded",
      validator: value => {
        return ["expanded", "compressed", "light"].indexOf(value) !== -1;
      },
    },
    /**
     * List of card logos
     */
    logos: {
      type: Array,
      default: () => [],
    },
    /**
     * Modifier code to change the accent color of the resource type icon. Possible values "docenti", "za", "zm", "zs", "studenti", "famiglie"
     */
    accentModifier: {
      type: String,
      default: "docenti",
    },
    /**
     * Modifier code to change the accent color on the action buttons. Possible values "docenti", "za", "zm", "zs", "studenti", "famiglie"
     */
    actionAccentModifier: {
      type: String,
      default: "default",
    },
    /**
     * Link assets via share_url instead of asset URL: this is useful if the resource json is stored somewhere as the asset URL expires after
     * a short period of time.
     */
    useShareLink: {
      type: Boolean,
      default: false,
    },
    /**
     * Show bookmark origin badge.
     */
    showBookmarkOrigin: {
      type: Boolean,
      default: false,
    },
    /**
     * Force opening method according to the given value (if a function, the function will be called instead of any opening method declared on the resource)
     */
    forceOpening: {
      type: [String, Function],
      default: null,
    },
    /**
     * Function that check the chips that correspond to the filters
     */
    checkActiveChip: {
      type: Function,
      default: () => ({}),
    },
    /**
     * Enable accordion info section
     */
    useAccordion: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      shareDisabledFormats: this.$root.config.shareDisabledFormats
        ? this.$root.config.shareDisabledFormats
        : ["modulo_google"],
      overlayTimeout: null,
      compressed: false,
      overlayVisible: false,
      dropdownShare: false,
      showConfirmDelete: false,
      url: "",
      isExpanded: false,
    };
  },
  computed: {
    /**
     * Check every type format against the list in shareDisabledFormats (which can be overwritten by setting shareDisabledFormats in appConf.js)
     */
    sharePermittedForFormats() {
      if (this.resource.type_format)
        return this.resource.type_format.filter(format => this.shareDisabledFormats.indexOf(format) === -1).length > 0;
      return false;
    },
    /**
     * Get the real share enabled status by executing the shareEnabled prop if function or its value if a scalar and check against list of permitted formats via sharePermittedForFormats.
     */
    shareEnabledComputed() {
      if (typeof this.shareEnabled === "function") {
        return this.shareEnabled(this.resource) && this.sharePermittedForFormats;
      }
      return this.shareEnabled && this.sharePermittedForFormats;
    },
    showCardInfos() {
      return (
        this.getHighlightText() ||
        this.resource.description ||
        this.hasChips ||
        (this.displayImage && this.layout === "compressed")
      );
    },
    hasChips() {
      return this.tag4List.length || this.tag3List.length;
    },
    origin() {
      if (this.bookmark) return this.bookmark.data_from;
      return "";
    },
    badgeAccentModifier() {
      if (this.origin && this.origin.code !== "sitolibro") return `dea-bg-target-${this.origin.code}`;
      return `dea-card-resource__badge--${this.accentModifier}`;
    },
    bookmarkButtonStatus() {
      return Boolean(this.bookmark);
    },
    tag1List() {
      if (typeof this.tag1 === "function") {
        return this.tag1(this.resource);
      }
      return this.extractTags(this.tag1);
    },
    tag2List() {
      if (typeof this.tag2 === "function") {
        return this.tag2(this.resource);
      }
      return this.extractTags(this.tag2);
    },
    tag3List() {
      if (typeof this.chips === "function") {
        return this.chips(this.resource);
      }
      const tagsList = this.chips.map(el => {
        return this.extractTags(el);
      });
      return tagsList.flat(1);
    },
    tag4List() {
      if (this.origin && this.showBookmarkOrigin) return [this.origin.label];
      if (typeof this.tag4 === "function") {
        return this.tag4(this.resource);
      }
      return this.extractTags(this.tag4);
    },
    showIcon() {
      if (typeof this.showTypeIcon === "function") {
        return this.showTypeIcon(this.resource);
      }
      return this.showTypeIcon;
    },
    cardClickEvent() {
      if (!this.multiResource && (this.mainAsset.full_url || this.isLocked)) {
        return "click";
      }
      return null;
    },
    layout() {
      if (this.cardLayout === "compressed" || this.compressed) {
        return "compressed";
      }
      return this.cardLayout;
    },
    isLocked() {
      return this.resource.isLocked || !this.resource.assets || this.resource.assets.length === 0;
    },
    mainAsset() {
      return this.linkedAssets.length ? this.linkedAssets[0] : { full_url: "", technical_type: "", learning_type: "" };
    },
    multiResource() {
      return this.resource.assets && this.resource.assets.length > 1;
    },
    linkedAssets() {
      return this.resource.assets && this.resource.assets.length
        ? this.resource.assets.filter(item => !item.locked && item.full_url)
        : [];
    },
    lockedAssets() {
      return this.resource.assets && this.resource.assets.length
        ? this.resource.assets.filter(item => item.locked || !item.full_url)
        : [];
    },
    iconAccentClass() {
      return `dea-card-resource__icon--${this.accentModifier}`;
    },
    actionAccentClass() {
      return `dea-card-resource__button--${this.actionAccentModifier}`;
    },
    badgeEmptyModifier() {
      return this.tag4 && this.tag4List.length === 0 ? "empty" : "";
    },
    teachersOnly() {
      return (
        (this.resource.target && JSON.stringify(this.resource.target) === '["teacher"]') ||
        this.extractTags("share_policy").includes("teacher_only")
      );
    },
    teachersResourceWarning() {
      return this.teachersOnly &&
        this.$root.config.share_resource_teacher_warning_message &&
        this.$root.config.share_resource_teacher_enabled
        ? this.$root.config.share_resource_teacher_warning_message
        : "";
    },
    displayImage() {
      if (this.resource.image) {
        return this.layout === "compressed" ? this.showImageOnCompressed : this.showImage;
      }
      return false;
    },
    usePopUnder() {
      return this.url && !this.overlayVisible;
    },
  },
  created() {
    window.addEventListener("resize", () => {
      if (window.innerWidth < variables.bpSizeMedium) {
        this.compressed = true;
      } else {
        this.compressed = false;
      }
    });
    window.addEventListener("hashchange", () => {
      this.overlayVisible = false;
    });
  },
  mounted() {
    try {
      // eslint-disable-next-line no-undef
      if (gapi) gapi.sharetoclassroom.go("dea-buttons-share");
    } catch {
      // eslint-disable-next-line no-unused-expressions
      null;
    }
  },
  methods: {
    saveBookmark() {
      this.$emit("save-bookmark", this.resource);
    },
    deleteBookmark() {
      this.$emit("delete-bookmark", this.bookmark);
      this.showConfirmDelete = false;
    },
    expandAccordion() {
      this.isExpanded = !this.isExpanded;
      this.$emit("accordion-expanded", { expanded: this.isExpanded, resource: this.resource.resource_uuid });
    },
    getLink(asset) {
      if (this.useShareLink) return this.resource.share_url;
      return asset.full_url;
    },
    getTechnicalType(resource) {
      if (resource.type_format && resource.type_format.length && Array.isArray(resource.type_format)) {
        return resource.type_format[0];
      }
      if (!Array.isArray(resource.type_format)) {
        return resource.type_format;
      }
      return resource.technical_type;
    },
    getLearningType(resource) {
      if (resource.type_format && resource.type_format.length && Array.isArray(resource.type_format)) {
        return resource.type_format[0];
      }
      if (!Array.isArray(resource.type_format)) {
        return resource.type_format;
      }
      return resource.technical_type;
    },
    extractTags(tagKey) {
      /**
       * Generic function to extract list of values from resource attributes.
       *
       * tagKey can be either:
       *
       * - a resource attribute
       * - a key of resource.meta_data
       *
       * if the matching element is and object, either its name or title properties are returned, otherwise the whole element is returned.
       */
      let data;
      if (tagKey && this.resource[tagKey] && this.resource[tagKey].length) {
        data = this.resource[tagKey];
      } else if (tagKey && this.resource.meta_data && this.resource.meta_data.length) {
        data = this.resource.meta_data.filter(item => item.code === tagKey).map(item => item.value);
      } else {
        return [];
      }
      if (data) {
        if (Array.isArray(data)) {
          const tagsList = data.map(item => {
            if (typeof item !== "object") return item;
            if ("name" in item) return item.name;
            if ("title" in item) return item.title;
            return item;
          });
          return tagsList;
        }
        return [data];
      }
      return [];
    },
    /**
     * Determine the resource opening method based on three information:
     *
     * - forceOpening prop: if set, the opening method is the prop value
     * - openingMethod resource attribute: if set, the opening method is the attribute value
     * - heuristic based on technicalType, learningType and resource content (check code for details, too complicated to summarize)
     */
    getOpeningMethod(url, technicalType = "", learningType = "") {
      const buster = Math.floor(Math.random() * 10000 + 1);
      let uniqueUrl;
      let customUrlHandler;
      let customPdfHandler;
      let contentData;
      let openingMethod = this.resource.open_mode;
      if (url.indexOf("?") > -1) uniqueUrl = `${url}&${buster}`;
      else uniqueUrl = `${url}?${buster}`;
      const openingData = { method: "overlay", url: uniqueUrl, duration: 5000 };
      if (this.forceOpening) openingMethod = this.forceOpening;
      if (openingMethod) {
        openingData.method = openingMethod;
        return openingData;
      }
      try {
        contentData = JSON.parse(this.resource.content);
        customUrlHandler = contentData.url;
        customPdfHandler = contentData.external_pdf;
      } catch {
        // ignoring the error, because content attribute it's not something we can use in the heuristic
      }

      const downloadType = ["external", "modulo_google", "zip"].indexOf(technicalType) > -1;
      const linkType = ["link", "link_web"].indexOf(technicalType) > -1;
      const linkLearning = ["link", "link_web", "video"].indexOf(learningType) > -1;
      const linkExtension = ["link"].indexOf(this.resource.extension) > -1;
      const ggbType = ["geogebra", "ggb"].indexOf(learningType) > -1;
      const pdfType =
        this.resource.assets &&
        this.resource.assets.map(asset => asset.filename).filter(filename => filename && filename.endsWith(".pdf"))
          .length > 0;
      const forceExternal = ["zip"].indexOf(technicalType) > -1;
      const newWindow =
        (downloadType && !customUrlHandler && !pdfType && !ggbType && !linkExtension) ||
        (linkType && linkLearning && !linkExtension) ||
        (pdfType && customPdfHandler);
      const forceWindow = uniqueUrl.startsWith("https://my.bsmart.it") || customUrlHandler;
      if (this.$root.config.debugResourceOpen) {
        /* eslint-disable no-console */
        console.log("new this.url ", this.url, uniqueUrl);
        console.log("new window", newWindow);
        console.log("downloadType", downloadType);
        console.log("contentData", contentData);
        console.log("customUrlHandler", customUrlHandler);
        console.log("customPdfHandler", customPdfHandler);
        console.log("technicalType", technicalType);
        console.log("learningType", learningType);
        console.log("pdfType", pdfType);
        console.log("ggbType", ggbType);
        console.log("resource", this.resource);
        /* eslint-enable no-console */
      }
      if (newWindow) {
        if (!forceExternal && (this.$root.config.open_external_new_window || forceWindow)) {
          openingData.method = "newTab";
        } else {
          openingData.method = "external";
        }
      } else {
        openingData.method = "overlay";
      }
      return openingData;
    },
    openResource(url, locked = false, technicalType = "", learningType = "") {
      const openingData = this.getOpeningMethod(url, technicalType, learningType);
      if (!locked) this.$emit("resource-opened", { resource: this.resource, url });
      if (locked) this.resourceLocked();
      else if (typeof openingData.method === "function") {
        openingData.method(this.resource);
      } else if (openingData.method === "internalLink") {
        window.location.href = openingData.url;
      } else if (openingData.method === "sameTab") {
        window.open(openingData.url, "_self");
      } else if (openingData.method === "newTab") {
        window.open(openingData.url);
      } else if (openingData.method === "external") {
        this.url = openingData.url;
        if (this.overlayTimeout) clearTimeout(this.overlayTimeout);
        this.overlayTimeout = setTimeout(() => {
          this.url = null;
        }, openingData.duration);
      } else if (openingData.method === "overlay") {
        this.overlayVisible = true;
        this.url = openingData.url;
      }
    },
    resourceLocked() {
      this.$emit("resource-locked", this.resource);
    },
    getHighlightText() {
      if (typeof this.highlightText === "function") {
        return this.highlightText(this.resource);
      }
      if (this.highlightText in this.resource) {
        return this.resource[this.highlightText];
      }
      return "";
    },
    closeOverlay() {
      this.overlayVisible = false;
      this.url = "";
    },
    showPipeSeparator(index) {
      return (
        (index === 0 && (this.cardLayout === "compressed" || this.tag1List.length > 1)) ||
        (index === 1 && this.tag1List.length > 2)
      );
    },
  },
};
</script>

<style lang="scss"></style>
